import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Coffee, Leaf, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { data } from '../data';

const Mindehoejtidelighed = () => {
  return (
    <div className="pt-0">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/mindehoejtidelighed-hero.png" 
            className="w-full h-full object-cover"
            alt="Mindehøjtidelighed hos GuldBoSund Konference & Event"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mt-20">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-light mb-6 uppercase tracking-wider"
          >
            Mindehøjtidelighed
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl font-light max-w-2xl mx-auto"
          >
            En smuk og værdig afsked i rolige rammer
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-8 uppercase tracking-wider">
                Nærvær, tryghed og omtanke
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed font-light">
                <p>
                  Vi forstår, at en mindehøjtidelighed er en personlig begivenhed, hvor både sorg og gode minder fylder. Derfor gør vi os umage for at skabe en atmosfære præget af nærvær, tryghed og omtanke.
                </p>
                <p>
                  Vores lokaler er lyse og venlige, omgivet af natur og ro – ideelle til at samles og mindes de stunder, I har delt.
                </p>
                <p className="font-medium text-gray-900">
                  En smuk og værdig afsked hos GuldBoSund Konference & Event
                </p>
                <p>
                  Hos os behøver I ikke bekymre jer om det praktiske. Vi sørger for alt fra opstilling af lokaler og klargøring til forplejning og afvikling. Det eneste, I skal gøre, er at komme og være sammen. Vi tager hånd om resten med respekt og professionalisme.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-stone-50 p-8 md:p-12 rounded-3xl border border-stone-100 shadow-sm"
            >
              <h3 className="text-2xl font-light mb-6 uppercase tracking-wider">Muligheder for forplejning</h3>
              <p className="text-gray-600 mb-8 leading-relaxed font-light">
                Vi tilbyder flere muligheder for forplejning, alt efter jeres ønsker og antal gæster. Vi hjælper med at finde den løsning, der passer bedst til netop jeres mindehøjtidelighed.
              </p>
              
              <ul className="space-y-6">
                {[
                  { icon: Coffee, title: "Kaffe & Kage", desc: "Enkel reception med friskbrygget kaffe, te og hjemmebag." },
                  { icon: Heart, title: "Let Anretning", desc: "Smørrebrød eller lette tapas-anretninger serveret med omtanke." },
                  { icon: Leaf, title: "Traditionel Middag", desc: "En varm middag i vores smukke lokaler med udsigt til fjorden." }
                ].map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                      <item.icon className="text-[#081f32]" size={20} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 uppercase tracking-widest text-sm">{item.title}</h4>
                      <p className="text-gray-600 text-sm font-light">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team/Support Section */}
      <section className="py-20 bg-[#081f32] text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-[#081f32] via-[#081f32]/40 to-transparent z-10" />
          <img 
            src="/images/nature-view.png" 
            className="w-full h-full object-cover opacity-20" 
            alt="" 
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-light mb-8 uppercase tracking-wider"
            >
              Vi er her for at hjælpe jer
            </motion.h2>
            <p className="text-xl text-stone-300 mb-10 leading-relaxed font-light">
              Vores team står klar til at lytte, vejlede og tilpasse arrangementet, så det bliver præcis, som I ønsker. Vi er her for at hjælpe jer igennem med ro, respekt og omsorg.
            </p>
            <div className="flex flex-wrap gap-6">
              <a 
                href={`tel:${data.contact.phone}`}
                className="flex items-center gap-3 bg-[#dab256] text-[#081f32] px-8 py-4 rounded-full text-xs font-light uppercase tracking-widest hover:bg-[#c4a04a] transition-all"
              >
                <Phone size={18} /> {data.contact.phone}
              </a>
              <Link 
                to="/kontakt" 
                className="flex items-center gap-3 bg-transparent border border-white text-white px-8 py-4 rounded-full text-xs font-light uppercase tracking-widest hover:bg-white/10 transition-all"
              >
                Kontakt os for en samtale <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Mindehoejtidelighed;
